import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GROUPS, PRESETS, STATUSES, TYPES, TYPE_IDS, typeDef } from '../featureTypes/registry';
import { geometryAnchor } from '../geo/measure';
import { syncStateOf, type FeatureRecord, type Status } from '../lib/types';
import { flyTo, isEditor, setState, useStore, type Overlays } from '../store/store';
import { Icon } from './Icon';

export function LeftPanel() {
  return (
    <aside className="left">
      <DrawSection />
      <LayersSection />
      <FiltersSection />
      <QueueSection />
    </aside>
  );
}

function DrawSection() {
  const { t } = useTranslation();
  const user = useStore((s) => s.user);
  const tool = useStore((s) => s.tool);
  const editor = isEditor(user);

  const byGroup = useMemo(() => {
    const out: Record<string, typeof PRESETS> = {};
    for (const p of PRESETS) (out[typeDef(p.type).group] ??= []).push(p);
    return out;
  }, []);

  return (
    <details open className="section">
      <summary>{editor ? t('left.draw') : t('left.tools')}</summary>
      <div className="tool-row">
        <button
          className={tool.kind === 'measure' ? 'active' : ''}
          onClick={() => setState({ tool: tool.kind === 'measure' ? { kind: 'none' } : { kind: 'measure' } })}
        >
          <Icon id="tool-measure" /> {t('tools.measure')}
        </button>
        <button
          className={tool.kind === 'pick' ? 'active' : ''}
          onClick={() => setState({ tool: tool.kind === 'pick' ? { kind: 'none' } : { kind: 'pick' } })}
        >
          <Icon id="tool-pick" /> {t('tools.pick')}
        </button>
      </div>
      {editor &&
        GROUPS.filter((g) => byGroup[g]).map((g) => (
          <div key={g} className="preset-group">
            <div className="group-label">{t(`groups.${g}`)}</div>
            <div className="preset-grid">
              {byGroup[g].map((p) => {
                const active = tool.kind === 'draw' && tool.presetId === p.id;
                const def = typeDef(p.type);
                return (
                  <button
                    key={p.id}
                    className={`preset${active ? ' active' : ''}`}
                    style={{ ['--c' as string]: def.color }}
                    title={t(`types.${p.type}`)}
                    onClick={() =>
                      setState({
                        tool: active
                          ? { kind: 'none' }
                          : { kind: 'draw', presetId: p.id, type: p.type, geometry: p.geometry },
                        editingShapeId: null,
                      })
                    }
                  >
                    <span className="swatch">
                      <Icon id={def.icon} size={14} />
                    </span>
                    {t(`presets.${p.id}`)}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
    </details>
  );
}

function LayersSection() {
  const { t } = useTranslation();
  const baseLayers = useStore((s) => s.baseLayers);
  const layerView = useStore((s) => s.layerView);
  const overlays = useStore((s) => s.overlays);
  const hasUnity = useStore((s) => Object.keys(s.unityObjects).length > 0);

  const setView = (id: string, patch: Partial<{ visible: boolean; opacity: number }>) => {
    const cur = layerView[id] ?? { visible: true, opacity: 1 };
    setState({ layerView: { ...layerView, [id]: { ...cur, ...patch } } });
  };
  const toggleOverlay = (k: keyof Overlays) => setState({ overlays: { ...overlays, [k]: !overlays[k] } });

  return (
    <details open className="section">
      <summary>{t('left.layers')}</summary>
      {baseLayers.length === 0 && <div className="muted small pad">{t('left.noBaseLayers')}</div>}
      {baseLayers.map((l) => {
        const v = layerView[l.id] ?? { visible: l.visibleByDefault, opacity: l.opacity || 1 };
        return (
          <div key={l.id} className="layer-row">
            <label>
              <input
                type="checkbox"
                checked={v.visible}
                onChange={() => setView(l.id, { visible: !v.visible })}
              />
              {l.name}
            </label>
            <input
              type="range"
              min={0.1}
              max={1}
              step={0.05}
              value={v.opacity}
              onChange={(e) => setView(l.id, { opacity: Number(e.target.value) })}
              aria-label={t('left.opacity')}
            />
          </div>
        );
      })}
      <div className="group-label">{t('left.overlays')}</div>
      {(Object.keys(overlays) as (keyof Overlays)[]).map((k) => (
        <label key={k} className="check-row">
          <input
            type="checkbox"
            checked={overlays[k]}
            onChange={() => toggleOverlay(k)}
            disabled={k === 'asBuilt' && !hasUnity}
          />
          {t(`overlays.${k}`)}
        </label>
      ))}
    </details>
  );
}

function FiltersSection() {
  const { t } = useTranslation();
  const filters = useStore((s) => s.filters);
  const features = useStore((s) => s.features);

  const counts = useMemo(() => {
    const byType: Record<string, number> = {};
    const byStatus: Record<string, number> = {};
    for (const f of Object.values(features)) {
      byType[f.type] = (byType[f.type] ?? 0) + 1;
      byStatus[f.status] = (byStatus[f.status] ?? 0) + 1;
    }
    return { byType, byStatus };
  }, [features]);

  const setAllTypes = (v: boolean) =>
    setState({ filters: { ...filters, types: Object.fromEntries(TYPE_IDS.map((x) => [x, v])) } });

  return (
    <details open className="section">
      <summary>{t('left.filters')}</summary>
      <div className="group-label">
        {t('left.featureTypes')}
        <span className="links">
          <button className="link" onClick={() => setAllTypes(true)}>
            {t('common.all')}
          </button>
          <button className="link" onClick={() => setAllTypes(false)}>
            {t('common.none')}
          </button>
        </span>
      </div>
      {GROUPS.map((g) => (
        <div key={g}>
          {TYPE_IDS.filter((id) => TYPES[id].group === g).map((id) => (
            <label key={id} className="check-row">
              <input
                type="checkbox"
                checked={filters.types[id] ?? true}
                onChange={() =>
                  setState({
                    filters: { ...filters, types: { ...filters.types, [id]: !(filters.types[id] ?? true) } },
                  })
                }
              />
              <span className="swatch sm" style={{ ['--c' as string]: TYPES[id].color }}>
                <Icon id={TYPES[id].icon} size={11} />
              </span>
              {t(`types.${id}`)}
              <span className="count">{counts.byType[id] ?? 0}</span>
            </label>
          ))}
        </div>
      ))}
      <div className="group-label">{t('left.statuses')}</div>
      {STATUSES.map((st) => (
        <label key={st} className="check-row">
          <input
            type="checkbox"
            checked={filters.statuses[st]}
            onChange={() =>
              setState({
                filters: { ...filters, statuses: { ...filters.statuses, [st]: !filters.statuses[st] } },
              })
            }
          />
          <span className={`status-pill ${st}`}>{t(`status.${st}`)}</span>
          <span className="count">{counts.byStatus[st] ?? 0}</span>
        </label>
      ))}
      <label className="check-row">
        <input
          type="checkbox"
          checked={filters.onlyMine}
          onChange={() => setState({ filters: { ...filters, onlyMine: !filters.onlyMine } })}
        />
        {t('left.onlyMine')}
      </label>
      <input
        className="full"
        placeholder={t('left.tagFilter')}
        value={filters.tag}
        onChange={(e) => setState({ filters: { ...filters, tag: e.target.value.trim() } })}
      />
    </details>
  );
}

/** Niklas's implementation queue: what needs doing in Unity. */
function QueueSection() {
  const { t } = useTranslation();
  const features = useStore((s) => s.features);
  const unityObjects = useStore((s) => s.unityObjects);

  const queue = useMemo(() => {
    const out: { f: FeatureRecord; reason: string }[] = [];
    for (const f of Object.values(features)) {
      if (f.type === 'note') continue;
      const sync = syncStateOf(f, unityObjects[f.unityKey]);
      if (f.status === 'approved') out.push({ f, reason: 'approved' });
      else if (f.status === 'needs_update') out.push({ f, reason: 'needs_update' });
      else if (sync === 'conflict' || sync === 'unity_ahead' || sync === 'missing_in_unity')
        out.push({ f, reason: sync });
    }
    const order: Record<string, number> = {
      conflict: 0,
      missing_in_unity: 1,
      needs_update: 2,
      unity_ahead: 3,
      approved: 4,
    };
    return out.sort(
      (a, b) => (order[a.reason] ?? 9) - (order[b.reason] ?? 9) || a.f.name.localeCompare(b.f.name),
    );
  }, [features, unityObjects]);

  return (
    <details open className="section">
      <summary>
        {t('left.queue')} <span className="count">{queue.length}</span>
      </summary>
      {queue.length === 0 && <div className="muted small pad">{t('left.queueEmpty')}</div>}
      {queue.map(({ f, reason }) => (
        <button
          key={f.id}
          className="queue-item"
          onClick={() => {
            setState({ selectedId: f.id });
            const a = geometryAnchor(f.geometry);
            if (a) flyTo(a.x, a.z);
          }}
        >
          <span className={`status-pill ${reason as Status}`}>
            {reason === 'approved' || reason === 'needs_update' ? t(`status.${reason}`) : t(`sync.${reason}`)}
          </span>
          <span className="ellipsis">{f.name}</span>
        </button>
      ))}
    </details>
  );
}
